import React, { ReactElement, useState, useEffect, } from 'react';
import { DI, DIProps } from '../../../../../Core';
import { TextField, ContextualSaveBar, FormLayout, Select, Button, Icon, BlockStack, Grid, Text, ButtonGroup, Divider, InlineStack, Banner } from '@shopify/polaris';
import { Switch } from 'antd';
import useContextualSaveBar, { isAdminLoggedIn } from '../../../Panel/index';
import { trendoptionprice, roundOffChoices, trendOptionBusinessPrice } from '../../../Settings/Template/Components/FilterProducts/FilterProductsHelper';
import { RefreshMajor } from '@shopify/polaris-icons';
import { getLastSyncInformation } from '../../ConfigHelper';
import { RenderSkeletonPriceSetting } from '../../skeleton/skeleton';
import CustomCalculator from '../../../CustomCalculator/CustomCalculator';
import AddDiscounts from './AddDiscount';
import { useTranslation } from 'react-i18next';
import { validatePriceEntries } from '../../../Editproduct/components/BusinessPrice/BusinessPrice';
import { DataToSend } from '../../../Listing Grid/Utilities/Helper';

interface Props extends DIProps {
    defaultAttributes: any;
    data: any;
    syncNecessaryInfo: () => void;
    syncConnectorInfo: () => void;
}

export const defaultPriceSettings: any = {
    "data_type": "price_settings",
    "settings_enabled": true,
    "settings_selected": {
        "sale_price": false,
        "business_price": false,
        "minimum_price": false
    },
    "standard_price": {
        "attribute": "default",
        "value": "",
        "price_roundoff_value": '',
    },
    "sale_price": {
        "attribute": "default",
        "value": "",
        "start_date": "",
        "end_date": "",
        "price_roundoff_value": '',
    },
    "business_price": {
        "attribute": "",
        "value": "",
        "price_roundoff_value": '',
        "fixed_discount": [
            {
                "quantity": "",
                "price": ""
            }
        ],
        "percentage_discount": [
            {
                "quantity": "",
                "price": ""
            }
        ],
        "quantity_type": "",
    },
    "minimum_price": {
        "attribute": "default",
        "value": "",
        "price_roundoff_value": '',
    }
};

export const defaultContextualSavebarData = {
    settings_enabled_switcher: false,
    standard_price_attribute: false,
    standard_price_value: false,
    standard_price_roundOff: false,
    sale_price_switcher: false,
    sale_price_attribute: false,
    sale_price_value: false,
    sale_price_roundOff: false,
    sale_price_start_date: false,
    sale_price_end_date: false,

    business_price_switcher: false,
    business_price_attribute: false,
    business_price_value: false,
    business_price_roundOff: false,
    business_price_quantity_discount: false,
    business_price_fixed_discount: false,
    business_price_percentage_discount: false,
    business_price_quantity_type: false,
    business_price_add_discount_btn: false,

    minimum_price_switcher: false,
    minimum_price_attribute: false,
    minimum_price_value: false,
    minimum_price_roundOff: false,
}

export const defaultPriceErrors: any = {
    standard_price: {
        attribute: false,
        value: false,
        price_roundoff: false,
    },
    sale_price: {
        attribute: false,
        value: false,
        start_date: false,
        end_date: false,
        price_roundoff: false,
    },
    business_price: {
        attribute: false,
        value: false,
        price_roundoff: false,
        "fixed_discount": [
            {
                "quantity": false,
                "price": false,
                "touched": {
                    "quantity": false,
                    "price": false,
                }
            }
        ],
        "percentage_discount": [
            {
                "quantity": false,
                "price": false,
                "touched": {
                    "quantity": false,
                    "price": false,
                }
            }
        ],
    },
    minimum_price: {
        attribute: false,
        value: false,
        price_roundoff: false,
    },
}

function InventorySettings(props: Props): ReactElement {
    const { t } = useTranslation();
    const roundOffOptions: any = [
        {
            title: t('Nearest Higher'),
            options: [
                { label: t("Whole number"), value: "higherWholeNumber" },
                { label: t("End with 9"), value: "higherEndWith9" },
                { label: t("End with 10"), value: "higherEndWith10" },
                { label: t("End with 0.49"), value: "higherEndWith0.49" },
                { label: t("End with 0.99"), value: "higherEndWith0.99" },
            ]
        },
        {
            title: 'Nearest Lower',
            options: [
                { label: t("Whole number"), value: "lowerWholeNumber" },
                { label: t("End with 9"), value: "lowerEndWith9" },
                { label: t("End with 10"), value: "lowerEndWith10" },
                { label: t("End with 0.49"), value: "lowerEndWith0.49" },
                { label: t("End with 0.99"), value: "lowerEndWith0.99" },
            ]
        },
        {
            title: t('No Rounding Off'),
            options: [
                { label: t("No round off"), value: "" },
            ]
        },
    ];

    const [roundOffUi, setRoundOffUi] = useState<any>({
        "standard_price": {
            "price_roundoff_value": ['0'],
        },
        "sale_price": {
            "price_roundoff_value": ['0'],
        },
        "business_price": {
            "price_roundoff_value": ['0'],
        },
        "minimum_price": {
            "price_roundoff_value": ['0'],
        }
    })
    const [activateModal, setActivateModal] = useState<any>({
        standard_price: false,
        sale_price: false,
        business_price: false,
        minimum_price: false
    })
    const [showBusinessPrice, setShowBusinessPrice] = useState<boolean>(false);
    const [savedShowBusinessPrice, setSavedShowBusinessPrice] = useState<boolean>(false);
    const { showSaveBar, hideSaveBar } = useContextualSaveBar(props);
    const [pageSkeleton, setPageSkeleton] = useState<boolean>(true);
    const [priceSettings, setPriceSettings] = useState<any>(JSON.parse(JSON.stringify(defaultPriceSettings)));
    const [savedPriceSettings, setSavedPriceSettings] = useState<any>(JSON.parse(JSON.stringify(defaultPriceSettings)));
    const [showContextualSaveBar, setShowContextualSaveBar] = useState<boolean>(false);
    const [saveLoader, setSaveLoader] = useState<boolean>(false);
    const [getLoader, setGetLoader] = useState<boolean>(false);
    const [errors, setErrors] = useState<any>({ ...defaultPriceErrors });
    const [contextualSaveBarData, setContextualSaveBarData] = useState<any>(JSON.parse(JSON.stringify(defaultContextualSavebarData)));
    const [lastSync, setLastSync] = useState('')

    let currentTime = new Date();
    let year = currentTime.getFullYear();
    let month = (currentTime.getMonth() + 1) < 10 ? '0' + (currentTime.getMonth() + 1) : currentTime.getMonth() + 1;
    let date = currentTime.getDate() < 10 ? '0' + currentTime.getDate() : currentTime.getDate();
    let currentDateInString = year + '-' + month + '-' + date;
    let start_time = new Date(priceSettings.sale_price.start_date);
    let start_year = start_time.getFullYear();
    let start_month = (start_time.getMonth() + 1) < 10 ? '0' + (start_time.getMonth() + 1) : start_time.getMonth() + 1;
    let start_date = start_time.getDate() < 10 ? '0' + start_time.getDate() : start_time.getDate();
    let startDateinString = start_year + '-' + start_month + '-' + start_date;

    const getLastPriceSyncDate = () => {
        let data = getLastSyncInformation(props, 'price');
        data.then((res: any) => {
            if (res?.error) props.error(t(res?.error) ?? '');
            else setLastSync(res?.date ?? '')
        })
    }

    const onPriceSettingsChange = (key: string, subKey: string, value: any) => {
        let tempPriceSettings = JSON.parse(JSON.stringify(priceSettings));
        if (subKey) {
            tempPriceSettings[key][subKey] = value;
        }
        else {
            tempPriceSettings[key] = value;
        }
        setPriceSettings(JSON.parse(JSON.stringify(tempPriceSettings)));
    }

    const onSettingsEnabledOrDisabled = (e: boolean) => {
        onPriceSettingsChange("settings_enabled", "", e);
        let tempContextualSaveBarData = JSON.parse(JSON.stringify(contextualSaveBarData));
        if (JSON.stringify(e) != JSON.stringify(savedPriceSettings['settings_enabled'])) tempContextualSaveBarData['settings_enabled_switcher'] = true;
        else tempContextualSaveBarData['settings_enabled_switcher'] = false;
        setContextualSaveBarData(JSON.parse(JSON.stringify(tempContextualSaveBarData)))
        if (!e) {
            setErrors(JSON.parse(JSON.stringify(defaultPriceErrors)));
        }
    }

    const getPriceSettings = async (from?: string) => {
        let { success, data, message } = await props.di.POST('connector/config/getConfig', {
            "group_code": ["price"]
        });
        if (success && data?.[0]?.['group_code'] == "price" && data?.[0]?.['value'] && Object.keys(data?.[0]?.['value'])?.length > 0) {
            let updatedDataValue: any = removeStaleValues(JSON.parse(JSON.stringify(data?.[0]?.['value'])));
            let tempPriceSettings = from == "save" ? JSON.parse(JSON.stringify(defaultPriceSettings)) : JSON.parse(JSON.stringify(priceSettings));
            let tempErrors = JSON.parse(JSON.stringify(errors));

            let tempRoundOffUi = JSON.parse(JSON.stringify(roundOffUi))
            if (updatedDataValue?.hasOwnProperty('data_type')) tempPriceSettings['data_type'] = updatedDataValue?.data_type
            if (updatedDataValue?.hasOwnProperty('settings_enabled')) tempPriceSettings['settings_enabled'] = updatedDataValue?.settings_enabled
            if (updatedDataValue?.hasOwnProperty('settings_selected')) tempPriceSettings['settings_selected'] = updatedDataValue?.settings_selected

            if (updatedDataValue?.hasOwnProperty('standard_price') && updatedDataValue?.standard_price?.hasOwnProperty('attribute')) tempPriceSettings['standard_price']['attribute'] = updatedDataValue?.standard_price?.attribute;
            if (updatedDataValue?.hasOwnProperty('standard_price') && updatedDataValue?.standard_price?.hasOwnProperty('value')) tempPriceSettings['standard_price']['value'] = updatedDataValue?.standard_price?.value;

            if (updatedDataValue?.hasOwnProperty('standard_price') && updatedDataValue?.standard_price?.hasOwnProperty('price_roundoff_value')) {
                tempPriceSettings['standard_price']['price_roundoff_value'] = updatedDataValue?.standard_price?.price_roundoff_value ?? '';
                tempRoundOffUi['standard_price']['price_roundoff_value'] = updatedDataValue?.standard_price?.price_roundoff_value ?? '';
            }

            if (updatedDataValue?.hasOwnProperty('sale_price') && updatedDataValue?.sale_price?.hasOwnProperty('attribute')) tempPriceSettings['sale_price']['attribute'] = updatedDataValue?.sale_price?.attribute;
            if (updatedDataValue?.hasOwnProperty('sale_price') && updatedDataValue?.sale_price?.hasOwnProperty('value')) tempPriceSettings['sale_price']['value'] = updatedDataValue?.sale_price?.value;
            if (updatedDataValue?.hasOwnProperty('sale_price') && updatedDataValue?.sale_price?.hasOwnProperty('start_date')) tempPriceSettings['sale_price']['start_date'] = updatedDataValue?.sale_price?.start_date;
            if (updatedDataValue?.hasOwnProperty('sale_price') && updatedDataValue?.sale_price?.hasOwnProperty('end_date')) tempPriceSettings['sale_price']['end_date'] = updatedDataValue?.sale_price?.end_date;

            if (updatedDataValue?.hasOwnProperty('sale_price') && updatedDataValue?.sale_price?.hasOwnProperty('price_roundoff_value')) {
                tempPriceSettings['sale_price']['price_roundoff_value'] = updatedDataValue?.sale_price?.price_roundoff_value ?? '';
                tempRoundOffUi['sale_price']['price_roundoff_value'] = updatedDataValue?.sale_price?.price_roundoff_value ?? '';
            }

            if (updatedDataValue?.hasOwnProperty('business_price') && updatedDataValue?.business_price?.hasOwnProperty('attribute')) tempPriceSettings['business_price']['attribute'] = updatedDataValue?.business_price?.attribute;
            if (updatedDataValue?.hasOwnProperty('business_price') && updatedDataValue?.business_price?.hasOwnProperty('value')) tempPriceSettings['business_price']['value'] = updatedDataValue?.business_price?.value;
            if (updatedDataValue?.hasOwnProperty('business_price') && updatedDataValue?.business_price?.hasOwnProperty('quantity_type')) {
                tempPriceSettings['business_price']['quantity_type'] = updatedDataValue?.business_price?.quantity_type;
                setShowBusinessPrice(true);
                setSavedShowBusinessPrice(true);
                if (updatedDataValue?.business_price?.quantity_discount) {
                    let err = updatedDataValue?.business_price?.quantity_discount.map((discountObj: any) => {
                        return {
                            "quantity": false,
                            "price": false,
                            "touched": {
                                "quantity": true,
                                "price": true,
                            }
                        }
                    })
                    if (updatedDataValue?.business_price?.quantity_type == 'fixed') {
                        tempPriceSettings = {
                            ...tempPriceSettings,
                            business_price: {
                                ...tempPriceSettings.business_price,
                                fixed_discount: updatedDataValue?.business_price?.quantity_discount
                            }
                        }

                        tempErrors.business_price.fixed_discount = err
                    }
                    else {
                        tempPriceSettings = {
                            ...tempPriceSettings,
                            business_price: {
                                ...tempPriceSettings.business_price,
                                percentage_discount: updatedDataValue?.business_price?.quantity_discount
                            }
                        }
                        tempErrors.business_price.percentage_discount = err
                    }
                }
            }

            if (updatedDataValue?.hasOwnProperty('business_price') && updatedDataValue?.business_price?.hasOwnProperty('price_roundoff_value')) {
                tempPriceSettings['business_price']['price_roundoff_value'] = updatedDataValue?.business_price?.price_roundoff_value ?? '';
                tempRoundOffUi['business_price']['price_roundoff_value'] = updatedDataValue?.business_price?.price_roundoff_value ?? '';
            }

            if (updatedDataValue?.hasOwnProperty('minimum_price') && updatedDataValue?.minimum_price?.hasOwnProperty('attribute')) tempPriceSettings['minimum_price']['attribute'] = updatedDataValue?.minimum_price?.attribute;
            if (updatedDataValue?.hasOwnProperty('minimum_price') && updatedDataValue?.minimum_price?.hasOwnProperty('value')) tempPriceSettings['minimum_price']['value'] = updatedDataValue?.minimum_price?.value;

            if (updatedDataValue?.hasOwnProperty('minimum_price') && updatedDataValue?.minimum_price?.hasOwnProperty('price_roundoff_value')) {
                tempPriceSettings['minimum_price']['price_roundoff_value'] = updatedDataValue?.minimum_price?.price_roundoff_value ?? '';
                tempRoundOffUi['minimum_price']['price_roundoff_value'] = updatedDataValue?.minimum_price?.price_roundoff_value ?? '';
            }
            setErrors(JSON.parse(JSON.stringify(tempErrors)))
            setPriceSettings(JSON.parse(JSON.stringify(tempPriceSettings)));
            setSavedPriceSettings(JSON.parse(JSON.stringify(tempPriceSettings)));
            setRoundOffUi(JSON.parse(JSON.stringify(tempRoundOffUi)))
        }
        // else props.error(t("Oops! Some error occured while getting price settings."));
        setPageSkeleton(false);
        setGetLoader(false);
    }

    const validator = () => {
        let hasError: boolean = false;
        let tempErrors = JSON.parse(JSON.stringify(errors));

        if (priceSettings.settings_enabled) {
            // Standard Price Validation
            if (priceSettings.standard_price.attribute !== "default") {
                tempErrors.standard_price.value = priceSettings.standard_price.value.trim() === "" || priceSettings.standard_price.value == priceSettings.standard_price.attribute;
                tempErrors.standard_price.attribute = priceSettings.standard_price.attribute === ""; //
                hasError ||= priceSettings.standard_price.value.trim() === "" || priceSettings.standard_price.attribute === "" || priceSettings.standard_price.value == priceSettings.standard_price.attribute;
            }

            // Sale Price Validation
            if (priceSettings.settings_selected.sale_price) {
                if (priceSettings.sale_price.attribute != "default") {
                    if (priceSettings.sale_price.attribute === "") {
                        tempErrors['sale_price']['attribute'] = priceSettings.sale_price.attribute === "";
                        if (!hasError) {
                            hasError = priceSettings.sale_price.attribute === "";
                        }
                    }
                    if (priceSettings.sale_price.value.trim() === "" || priceSettings.sale_price.value === priceSettings.sale_price.attribute) {
                        tempErrors['sale_price']['value'] = priceSettings.sale_price.value.trim() === "" || priceSettings.sale_price.value === priceSettings.sale_price.attribute;
                        if (!hasError) {
                            hasError = priceSettings.sale_price.value.trim() === "" || priceSettings.sale_price.value === priceSettings.sale_price.attribute;
                        }
                    }
                }

                if ((priceSettings.sale_price.start_date !== savedPriceSettings.sale_price.start_date || priceSettings.sale_price.start_date === "") && (priceSettings.sale_price.start_date === "" || new Date(priceSettings.sale_price.start_date) > new Date(priceSettings.sale_price.end_date) && priceSettings.sale_price.start_date < currentDateInString)) {
                    tempErrors['sale_price']['start_date'] = priceSettings.sale_price.start_date === "" || new Date(priceSettings.sale_price.start_date) > new Date(priceSettings.sale_price.end_date) || priceSettings.sale_price.start_date < currentDateInString;
                    if (!hasError) {
                        hasError = (priceSettings.sale_price.start_date === "") || (new Date(priceSettings.sale_price.start_date) > new Date(priceSettings.sale_price.end_date)) || (priceSettings.sale_price.start_date < currentDateInString);
                    }
                }

                if ((priceSettings.sale_price.end_date === "" || new Date(priceSettings.sale_price.start_date) > new Date(priceSettings.sale_price.end_date))) {
                    tempErrors['sale_price']['end_date'] = priceSettings.sale_price.end_date === "" || new Date(priceSettings.sale_price.start_date) > new Date(priceSettings.sale_price.end_date);

                    if (!hasError) {
                        hasError = priceSettings.sale_price.end_date === "" || new Date(priceSettings.sale_price.start_date) > new Date(priceSettings.sale_price.end_date);
                    }
                }
            }

            // Business Price Validation
            if (priceSettings.settings_selected.business_price) {
                if (priceSettings.business_price.attribute !== "default") {
                    tempErrors.business_price.value = priceSettings.business_price.value === "" || priceSettings.business_price.value == priceSettings.business_price.attribute;
                    tempErrors.business_price.attribute = priceSettings.business_price.attribute === "";
                    if (!hasError) hasError = priceSettings.business_price.value === "" || priceSettings.business_price.attribute === '' || priceSettings.business_price.value == priceSettings.business_price.attribute;
                }

                if (showBusinessPrice) {
                    if (!priceSettings.business_price.quantity_type) {
                        tempErrors.business_price.quantity_type = true;
                        hasError = true;
                    }

                    if (priceSettings.business_price.quantity_type == "fixed") {
                        let validationResult = validatePriceEntries(
                            priceSettings.business_price,
                            {},
                            () => { },
                            "global", // Replace with relevant value if needed
                            priceSettings.business_price.fixed_discount,
                            tempErrors.business_price.fixed_discount,
                            "save",
                            t
                        );

                        tempErrors.business_price.fixed_discount = JSON.parse(JSON.stringify(validationResult));
                        if (!hasError) hasError = validationResult.some((error: any) => error.hasError);
                    }

                    if (priceSettings.business_price.quantity_type == "percent") {
                        let validationResult = validatePriceEntries(
                            priceSettings.business_price,
                            {},
                            () => { },
                            "global", // Replace with relevant value if needed
                            priceSettings.business_price.percentage_discount,
                            tempErrors.business_price.percentage_discount,
                            "save",
                            t
                        );

                        tempErrors.business_price.percentage_discount = JSON.parse(JSON.stringify(validationResult));
                        if (!hasError) hasError = validationResult.some((error: any) => error.hasError);
                    }
                }
            }


            // Minimum Price Validation
            if (priceSettings.settings_selected.minimum_price) {
                if (priceSettings.minimum_price.attribute !== "default") {
                    tempErrors.minimum_price.value = priceSettings.minimum_price.value.trim() === "" || priceSettings.minimum_price.value == priceSettings.minimum_price.attribute;
                    tempErrors.minimum_price.attribute = priceSettings.minimum_price.attribute === ""; //
                    if (!hasError)
                        hasError = priceSettings.minimum_price.value.trim() === "" || priceSettings.minimum_price.attribute === '' || priceSettings.minimum_price.value == priceSettings.minimum_price.attribute;
                }
            }
        }
        return { hasError, errorsObj: tempErrors };
    }

    const removeStaleValues = (obj: any) => {
        let updatedData: any = JSON.parse(JSON.stringify({ ...defaultPriceSettings, ...obj }));
        if (!updatedData.settings_enabled) {
            updatedData = JSON.parse(JSON.stringify(defaultPriceSettings));
            updatedData['settings_enabled'] = false;
        }
        else {
            if (updatedData?.standard_price?.attribute == "default") {
                updatedData.standard_price.value = "";
            }
            if (!updatedData?.settings_selected?.sale_price) {
                updatedData.sale_price = JSON.parse(JSON.stringify(defaultPriceSettings.sale_price));
            }
            else {
                if (updatedData?.sale_price.attribute == "default") {
                    updatedData.sale_price.value = "";
                }
            }
            if (!updatedData?.settings_selected?.business_price) {
                updatedData.business_price = JSON.parse(JSON.stringify(defaultPriceSettings.business_price));
            }
            else {
                if (updatedData?.business_price?.attribute == "default") {
                    updatedData.business_price.value = "";
                }
            }

            if (!updatedData?.settings_selected?.minimum_price) {
                updatedData.minimum_price = JSON.parse(JSON.stringify(defaultPriceSettings.minimum_price));
            }
            else {
                if (updatedData?.minimum_price?.attribute == "default") {
                    updatedData.minimum_price.value = "";
                }
            }
        }
        return updatedData;
    }

    const filterEditedData = (original: any, changed: any) => {
        const allSettings = Object.keys(defaultPriceSettings);
        const myObj: any = {};
        allSettings.forEach((setting: string) => {
            if (JSON.stringify(changed[setting]) != JSON.stringify(original[setting])) {
                myObj[setting] = changed[setting];
            }
        });
        return myObj;
    }

    const prepareBackendData = () => {
        let updatedPreparedData = removeStaleValues(JSON.parse(JSON.stringify(priceSettings)));
        updatedPreparedData.business_price.quantity_discount = updatedPreparedData.business_price.quantity_type == "fixed" ? updatedPreparedData.business_price.fixed_discount : updatedPreparedData.business_price.percentage_discount;
        delete updatedPreparedData.business_price.fixed_discount;
        delete updatedPreparedData.business_price.percentage_discount;

        const editedData = JSON.parse(JSON.stringify(filterEditedData(savedPriceSettings, updatedPreparedData)));
        let backendData = [
            {
                data: editedData,
                group_code: "price"
            }
        ];
        return backendData;
    }

    const savePriceSettings = () => {
        setSaveLoader(true);
        const { hasError, errorsObj } = validator();

        if (!hasError) {
            let backendData = prepareBackendData();
            props.di.POST('connector/config/saveConfig', {
                "data": backendData
            }).then((response: any) => {
                setShowContextualSaveBar(false);
                setContextualSaveBarData(JSON.parse(JSON.stringify(defaultContextualSavebarData)));
                setSaveLoader(false);
                if (response?.success) {
                    props.success(t("Price settings saved successfully."));
                    setGetLoader(true);
                    getPriceSettings("save");
                    getLastPriceSyncDate();
                    setErrors(JSON.parse(JSON.stringify(defaultPriceErrors)));
                }
                else { //api call mein agar koi error hogi
                    props.error(t("Oops! Some error occured while saving."));
                }
            })
        }
        else {
            props.error(t("Some information is missing or wrong, kindly check them before proceeding."));
            setErrors(JSON.parse(JSON.stringify(errorsObj)));
            setSaveLoader(false);
        }
    }

    const handleDiscardBtn = () => {
        setErrors(JSON.parse(JSON.stringify(defaultPriceErrors)));
        setPageSkeleton(true);
        setShowContextualSaveBar(false);
        setPriceSettings(JSON.parse(JSON.stringify(defaultPriceSettings)));
        setGetLoader(true);
        getPriceSettings();
        getLastPriceSyncDate();
    }

    useEffect(() => {
        setGetLoader(true);
        getPriceSettings();
        getLastPriceSyncDate();
        return () => {
            if (process.env.REACT_APP_PROD === "1" && !isAdminLoggedIn()) hideSaveBar();
            setContextualSaveBarData({});
            setErrors(JSON.parse(JSON.stringify(defaultPriceErrors)));
        };
    }, []);

    useEffect(() => {
        let show = false;
        Object.values(contextualSaveBarData).map(val => {
            if (val) show = true;
        });
        setShowContextualSaveBar(show);
    }, [contextualSaveBarData]);

    //Contextual Save Bar of App Bridge
    if (process.env.REACT_APP_PROD === "1" && !isAdminLoggedIn()) {
        let savebar_options: any = {
            loading: saveLoader,
        };
        if (showContextualSaveBar) {
            showSaveBar(handleDiscardBtn, savePriceSettings, { ...savebar_options });
        } else hideSaveBar();
    }
    //prev code
    useEffect(() => {
        let tempErrors: any = JSON.parse(JSON.stringify(errors));
        const discountType = priceSettings?.business_price?.quantity_type == "fixed" ? "fixed_discount" : "percentage_discount";
        tempErrors.business_price[discountType] = validatePriceEntries(priceSettings?.business_price, {}, () => { }, "global", priceSettings?.business_price?.[discountType], JSON.parse(JSON.stringify(errors.business_price[discountType])), "update", t);
        setErrors(JSON.parse(JSON.stringify(tempErrors)));
    }, [priceSettings?.business_price])

    const [B2B_visible, setB2B_visible] = useState<boolean | undefined>(undefined)

    useEffect(() => {
        let payload = {
            ...DataToSend(props),
            "data": {
                "barcode_exemption": false,
                "product_type": "PRODUCT",
                "show_category": true
            }
        }
        props.di.POST("connector/profile/getCategoryAttributes", payload).then((response: any) => {
            if (response.success) {
                setB2B_visible(response?.data?.attributes?.offer?.Optional?.purchasable_offer?.items?.properties?.audience?.enum?.includes("B2B"))
            }
        })

    }, [])



    return <>
        {pageSkeleton ? RenderSkeletonPriceSetting() : <>
            {
                showContextualSaveBar && (process.env.REACT_APP_PROD === "0" ||
                    isAdminLoggedIn()) && (
                    <ContextualSaveBar
                        message={t("Unsaved changes")}
                        saveAction={{
                            onAction: () => savePriceSettings(),
                            loading: saveLoader,
                            disabled: false,
                        }}
                        discardAction={{
                            onAction: () => { handleDiscardBtn() },
                        }}
                    />
                )
            }
            <BlockStack gap="500">
                <Grid columns={{ xs: 1, sm: 1, md: 3, lg: 3, xl: 3 }} >
                    <Grid.Cell columnSpan={{ xs: 1, sm: 1, md: 2, lg: 2, xl: 2 }} >
                        <BlockStack gap="200">
                            <Text variant="headingMd" as="h6" id='price_Text_PriceSettings'>{t("Price Settings")}</ Text>
                            <Text as="p" id='price_Text_PriceSettingsDescription' fontWeight="regular" tone='subdued'>{t("Adjust your product prices, including custom, sale, business, and minimum prices, to achieve a personalized and competitive market presence.")}</Text>
                        </BlockStack>
                    </Grid.Cell>
                    <Grid.Cell columnSpan={{ xs: 1, sm: 1, md: 1, lg: 1, xl: 1 }}>
                        <div className='Custom__Button--Alignment'>
                            <ButtonGroup variant="segmented">
                                <Button id='price_Btn_Enable' disabled={saveLoader || getLoader} pressed={priceSettings.settings_enabled} onClick={() => { onSettingsEnabledOrDisabled(true) }}>{t("Enabled")}</Button>
                                <Button id='price_Btn_Disable' disabled={saveLoader || getLoader} pressed={!priceSettings.settings_enabled} onClick={() => { onSettingsEnabledOrDisabled(false) }}>{t("Disabled")}</Button>
                            </ButtonGroup>
                        </div>
                    </Grid.Cell>
                </Grid>
                <Divider />
                <div className={!priceSettings.settings_enabled ? "card_dull" : undefined}><CustomCalculator {...props} /></div>
                <Banner>{t("The price structure flows from the Standard Price to the Business Price, followed by Quantity Tiers 1 through 5, and concludes with the Minimum Price.")}</Banner>
                <InlineStack gap="200" wrap={false}>
                    <Switch className='switcherBtn' size='small' disabled defaultChecked id='price_Switch_StandardPrice' />
                    <div className='Custom__Fill--Alignment'>
                        <BlockStack gap="200">
                            <BlockStack gap="100">
                                <div className='Custom__Line--Height'>
                                    <Text variant="headingSm" as="h6" id='price_Text_StandardPrice'>{t("Set Product Price")}</Text>
                                </div>
                                <Text as="p" fontWeight="regular" tone='subdued' id='price_Text_StandardPriceDescription'>{t("This is the price at which the item will be sold when there are no ongoing sales or applicable discounts.")}</Text>
                            </BlockStack>
                            <div className='Custom__TextField--alignmnet'>
                                <FormLayout>
                                    <FormLayout.Group condensed>
                                        <Select
                                            id='price_Select_StandardPrice_PricingTrend'
                                            // label='{t("Pricing Trend")}'
                                            label={!priceSettings.settings_enabled || saveLoader || getLoader ? <Text as='p' tone='subdued'>{t("Pricing Trend")}<Text as='span' tone='critical'>*</Text></Text> : <Text as='p'>{t("Pricing Trend")}<Text as='span' tone='critical'>*</Text></Text>}
                                            options={trendoptionprice(t)}
                                            onChange={(e) => {
                                                onPriceSettingsChange("standard_price", "attribute", e);
                                                let tempErrors = JSON.parse(JSON.stringify(errors));
                                                tempErrors['standard_price']['attribute'] = e == "";
                                                tempErrors['standard_price']['value'] = false;
                                                setErrors(JSON.parse(JSON.stringify(tempErrors)));

                                                let tempContextualSaveBarData = JSON.parse(JSON.stringify(contextualSaveBarData));
                                                if (JSON.stringify(e) != JSON.stringify(savedPriceSettings['standard_price']['attribute'])) tempContextualSaveBarData['standard_price_attribute'] = true;
                                                else {
                                                    tempContextualSaveBarData['standard_price_value'] = false;
                                                    tempContextualSaveBarData['standard_price_attribute'] = false;
                                                }
                                                setContextualSaveBarData(JSON.parse(JSON.stringify(tempContextualSaveBarData)))
                                            }}
                                            value={priceSettings.standard_price.attribute}
                                            disabled={!priceSettings.settings_enabled || saveLoader || getLoader}
                                            error={errors?.standard_price?.attribute}
                                        />
                                        {priceSettings.standard_price.attribute != "" && priceSettings.standard_price.attribute !== "default" &&
                                            <TextField
                                                id='price_TextField_StandardPrice_PriceValue'
                                                label={!priceSettings.settings_enabled || saveLoader || getLoader ? <Text as='p' tone='subdued'>{t("Price Value")}<Text as='span' tone='critical'>*</Text></Text> : <Text as='p'>{t("Price Value")}<Text as='span' tone='critical'>*</Text></Text>}
                                                autoComplete='off'
                                                onChange={(e: any) => {
                                                    e = e.replace(/[\sa-zA-Z-!$%^&*()@_+|~=`\\#{}\[\]:";'<>?,\/]/gi, '');
                                                    e = e?.[0] === '.' ? '0' + e : e;
                                                    e = (e.split(".").length - 1 > 1 ? e.slice(0, -1) : e).replace(/^(\d+.?\d{0,2})\d*$/, "$1");
                                                    onPriceSettingsChange("standard_price", "value", e);
                                                    let tempErrors = JSON.parse(JSON.stringify(errors));
                                                    tempErrors['standard_price']['value'] = e == "";
                                                    setErrors(JSON.parse(JSON.stringify(tempErrors)));

                                                    let tempContextualSaveBarData = JSON.parse(JSON.stringify(contextualSaveBarData));
                                                    if (JSON.stringify(e) != JSON.stringify(savedPriceSettings['standard_price']['value'])) tempContextualSaveBarData['standard_price_value'] = true;
                                                    else tempContextualSaveBarData['standard_price_value'] = false;
                                                    setContextualSaveBarData(JSON.parse(JSON.stringify(tempContextualSaveBarData)));
                                                }}
                                                placeholder={t("Enter value")}
                                                value={priceSettings.standard_price.value}
                                                disabled={!priceSettings.settings_enabled || saveLoader || getLoader}
                                                error={errors?.standard_price?.value}
                                            />
                                        }
                                        {priceSettings.standard_price.attribute != '' && <Select
                                            id='price_Select_StandardPrice_RoundingOff'
                                            // label={<Text as='span'>{t("Rounding Off")}<Text as='span' tone='critical'> *</Text></Text>}
                                            label={!priceSettings.settings_enabled || saveLoader || getLoader ? <Text as='p' tone='subdued'>{t("Rounding Off")}<Text as='span' tone='critical'>*</Text></Text> : <Text as='p'>{t("Rounding Off")}<Text as='span' tone='critical'>*</Text></Text>}
                                            options={roundOffOptions}
                                            onChange={(e) => {
                                                onPriceSettingsChange("standard_price", "price_roundoff_value", e);
                                                let tempContextualSaveBarData = JSON.parse(JSON.stringify(contextualSaveBarData));
                                                if (JSON.stringify(e) != JSON.stringify(savedPriceSettings['standard_price']['price_roundoff_value'])) tempContextualSaveBarData['standard_price' + '_roundOff'] = true;
                                                else tempContextualSaveBarData['standard_price' + '_roundOff'] = false;
                                                setContextualSaveBarData(JSON.parse(JSON.stringify(tempContextualSaveBarData)));
                                            }}
                                            value={priceSettings?.standard_price?.price_roundoff_value}
                                            disabled={!priceSettings.settings_enabled || saveLoader || getLoader}
                                        // helpText={`Kinldy use 'Preview price-round off' feature for exact calculation. `}
                                        />}
                                    </FormLayout.Group>
                                </FormLayout>
                            </div>
                        </BlockStack>
                    </div>
                </InlineStack>
                <InlineStack gap="200" wrap={false}>
                    <Switch
                        className='switcherBtn'
                        size='small'
                        id='price_Switch_SalePrice'
                        checked={priceSettings.settings_selected.sale_price}
                        onChange={(e) => {
                            onPriceSettingsChange("settings_selected", "sale_price", e);
                            let tempContextualSaveBarData = JSON.parse(JSON.stringify(contextualSaveBarData));
                            if (JSON.stringify(e) != JSON.stringify(savedPriceSettings['settings_selected']['sale_price'])) tempContextualSaveBarData['sale_price_switcher'] = true;
                            else tempContextualSaveBarData['sale_price_switcher'] = false
                            if (!e) {
                                tempContextualSaveBarData['sale_price_attribute'] = false;
                                tempContextualSaveBarData['sale_price_value'] = false;
                                tempContextualSaveBarData['sale_price_roundOff'] = false;
                                tempContextualSaveBarData['sale_price_start_date'] = false;
                                tempContextualSaveBarData['sale_price_end_date'] = false;

                                let tempErrors = JSON.parse(JSON.stringify(errors));
                                tempErrors['sale_price'] = defaultPriceErrors['sale_price'];
                                setErrors(JSON.parse(JSON.stringify(tempErrors)));
                            }
                            else {
                                if (JSON.stringify(savedPriceSettings['sale_price']['attribute']) != JSON.stringify(priceSettings['sale_price']['attribute'])) tempContextualSaveBarData['sale_price_attribute'] = true;
                                else tempContextualSaveBarData['sale_price_attribute'] = false;

                                if (JSON.stringify(savedPriceSettings['sale_price']['value']) != JSON.stringify(priceSettings['sale_price']['value'])) tempContextualSaveBarData['sale_price_value'] = true;
                                else tempContextualSaveBarData['value'] = false;

                                if (JSON.stringify(savedPriceSettings['sale_price']['price_roundoff_value']) != JSON.stringify(priceSettings['sale_price']['price_roundoff_value'])) tempContextualSaveBarData['sale_price_roundOff'] = true;
                                else tempContextualSaveBarData['sale_price_roundOff'] = false;

                                if (JSON.stringify(savedPriceSettings['sale_price']['start_date']) != JSON.stringify(priceSettings['sale_price']['start_date'])) tempContextualSaveBarData['sale_price_start_date'] = true;
                                else tempContextualSaveBarData['sale_price_start_date'] = false;

                                if (JSON.stringify(savedPriceSettings['sale_price']['end_date']) != JSON.stringify(priceSettings['sale_price']['end_date'])) tempContextualSaveBarData['sale_price_end_date'] = true;
                                else tempContextualSaveBarData['sale_price_end_date'] = false;
                            }
                            setContextualSaveBarData(JSON.parse(JSON.stringify(tempContextualSaveBarData)));
                        }}
                        disabled={!priceSettings.settings_enabled || saveLoader || getLoader} />
                    <div className='Custom__Fill--Alignment'>
                        <BlockStack gap="200">
                            <BlockStack gap="100">
                                <div className='Custom__Line--Height'>
                                    <Text variant="headingSm" as="h6" id='price_Text_SalePrice'>{t("Set Sale Price")}</Text>
                                </div>
                                <Text id='price_Text_SalePriceDescription' as="p" fontWeight="regular" tone='subdued'>{t("Establish discounts for your products during a current or upcoming season.")}</Text>
                            </BlockStack>
                            <div className='Custom__TextField--alignmnet'>
                                <BlockStack gap="200">
                                    <FormLayout>
                                        <FormLayout.Group condensed>
                                            <Select
                                                id='price_Select_SalePrice_PricingTrend'
                                                label={!priceSettings.settings_enabled || saveLoader || getLoader || !priceSettings.settings_selected.sale_price ? <Text as='p' tone='subdued'>{t("Pricing Trend")}<Text as='span' tone='critical'>*</Text></Text> : <Text as='p'>{t("Pricing Trend")}<Text as='span' tone='critical'>*</Text></Text>}
                                                options={trendoptionprice(t)}
                                                onChange={(e) => {
                                                    onPriceSettingsChange("sale_price", "attribute", e);
                                                    let tempErrors = JSON.parse(JSON.stringify(errors));
                                                    tempErrors['sale_price']['attribute'] = e == "";
                                                    tempErrors['sale_price']['value'] = false;
                                                    setErrors(JSON.parse(JSON.stringify(tempErrors)));

                                                    let tempContextualSaveBarData = JSON.parse(JSON.stringify(contextualSaveBarData));
                                                    if (JSON.stringify(e) != JSON.stringify(savedPriceSettings?.sale_price?.attribute)) tempContextualSaveBarData['sale_price_attribute'] = true;
                                                    else tempContextualSaveBarData['sale_price_attribute'] = false;
                                                    setContextualSaveBarData(JSON.parse(JSON.stringify(tempContextualSaveBarData)));
                                                }}
                                                value={priceSettings.sale_price.attribute}
                                                error={errors?.sale_price?.attribute}
                                                disabled={!priceSettings.settings_enabled || saveLoader || getLoader || !priceSettings.settings_selected.sale_price}
                                            />
                                            {priceSettings.sale_price.attribute !== "" && priceSettings.sale_price.attribute !== "default" &&
                                                <TextField
                                                    id='price_TextField_SalePrice_PriceValue'
                                                    label={!priceSettings.settings_enabled || saveLoader || getLoader || !priceSettings.settings_selected.sale_price ? <Text as='p' tone='subdued'>{t("Price Value")}<Text as='span' tone='critical'>*</Text></Text> : <Text as='p'>{t("Price Value")}<Text as='span' tone='critical'>*</Text></Text>}
                                                    autoComplete='off'
                                                    onChange={(e) => {
                                                        e = e.replace(/[\sa-zA-Z-!$%^&*()@_+|~=`\\#{}\[\]:";'<>?,\/]/gi, '');
                                                        e = e?.[0] === '.' ? '0' + e : e;
                                                        e = (e.split(".").length - 1 > 1 ? e.slice(0, -1) : e).replace(/^(\d+.?\d{0,2})\d*$/, "$1");
                                                        onPriceSettingsChange("sale_price", "value", e)
                                                        let tempErrors = JSON.parse(JSON.stringify(errors));
                                                        tempErrors['sale_price']['value'] = e == "";
                                                        setErrors(JSON.parse(JSON.stringify(tempErrors)));

                                                        let tempContextualSaveBarData = JSON.parse(JSON.stringify(contextualSaveBarData));
                                                        if (JSON.stringify(e) != JSON.stringify(savedPriceSettings?.sale_price?.value)) tempContextualSaveBarData['sale_price_value'] = true;
                                                        else tempContextualSaveBarData['sale_price_value'] = false;
                                                        setContextualSaveBarData(JSON.parse(JSON.stringify(tempContextualSaveBarData)));

                                                    }}
                                                    placeholder={t("Enter value")}
                                                    value={priceSettings.sale_price.value}
                                                    error={errors?.sale_price.value}
                                                    disabled={!priceSettings.settings_enabled || saveLoader || getLoader || !priceSettings.settings_selected.sale_price}
                                                />
                                            }
                                            {priceSettings.sale_price.attribute != '' && <Select
                                                id='price_Select_SalePrice_RoundingOff'
                                                label={!priceSettings.settings_enabled || saveLoader || getLoader || !priceSettings.settings_selected.sale_price ? <Text as='p' tone='subdued'>{t("Rounding Off")}<Text as='span' tone='critical'>*</Text></Text> : <Text as='p'>{t("Rounding Off")}<Text as='span' tone='critical'>*</Text></Text>}
                                                // label={props.priceSettings.settings_enabled ? <Text as='span'>{t("Rounding Off")}<Text as='span' tone='critical'> *</Text></Text> : <Text as='span' tone="subdued">{t("Rounding Off")}<Text as='span' tone='critical'> *</Text></Text>}
                                                options={roundOffOptions}
                                                onChange={(e) => {
                                                    onPriceSettingsChange("sale_price", "price_roundoff_value", e);
                                                    let tempContextualSaveBarData = JSON.parse(JSON.stringify(contextualSaveBarData));
                                                    if (JSON.stringify(e) != JSON.stringify(savedPriceSettings['sale_price']['price_roundoff_value'])) tempContextualSaveBarData['sale_price' + '_roundOff'] = true;
                                                    else tempContextualSaveBarData['sale_price' + '_roundOff'] = false;
                                                    setContextualSaveBarData(JSON.parse(JSON.stringify(tempContextualSaveBarData)));
                                                }}
                                                value={priceSettings?.sale_price?.price_roundoff_value}
                                                // helpText={`Kinldy use 'Preview price-round off' feature for exact calculation. `}
                                                disabled={!priceSettings.settings_enabled || saveLoader || getLoader || !priceSettings.settings_selected.sale_price}
                                            // error={context.errors.pricing_settings.standard_price.attribute}
                                            />
                                            }
                                        </FormLayout.Group>
                                    </FormLayout>
                                    <FormLayout>
                                        <FormLayout.Group>
                                            <TextField
                                                id='price_TextField_SalePrice_StartDate'
                                                label={!priceSettings.settings_enabled || saveLoader || getLoader || !priceSettings.settings_selected.sale_price ? <Text as='p' tone='subdued'>{t("Start date")}<Text as='span' tone='critical'>*</Text></Text> : <Text as='p'>{t("Start date")}<Text as='span' tone='critical'>*</Text></Text>}
                                                autoComplete='off'
                                                onChange={(e) => {
                                                    onPriceSettingsChange("sale_price", "start_date", e)
                                                    let tempErrors = JSON.parse(JSON.stringify(errors));
                                                    tempErrors['sale_price']['start_date'] = e == "" || new Date(new Date().toISOString().substring(0, 10)) > new Date(e);
                                                    tempErrors['sale_price']['end_date'] = (new Date(e) > new Date(priceSettings.sale_price.start_date))
                                                    setErrors(JSON.parse(JSON.stringify(tempErrors)));

                                                    let tempContextualSaveBarData = JSON.parse(JSON.stringify(contextualSaveBarData));
                                                    if (JSON.stringify(e) != JSON.stringify(savedPriceSettings?.sale_price?.start_date)) tempContextualSaveBarData['sale_price_start_date'] = true;
                                                    else tempContextualSaveBarData['sale_price_start_date'] = false;
                                                    setContextualSaveBarData(JSON.parse(JSON.stringify(tempContextualSaveBarData)));

                                                }}
                                                type="date"
                                                value={priceSettings.sale_price.start_date}
                                                min={currentDateInString}
                                                error={errors?.sale_price?.start_date}
                                                disabled={!priceSettings.settings_enabled || saveLoader || getLoader || !priceSettings.settings_selected.sale_price}

                                            />
                                            <TextField
                                                id='price_TextField_SalePrice_EndDate'
                                                label={!priceSettings.settings_enabled || saveLoader || getLoader || !priceSettings.settings_selected.sale_price ? <Text as='p' tone='subdued'>{t("End date")}<Text as='span' tone='critical'>*</Text></Text> : <Text as='p'>{t("End date")}<Text as='span' tone='critical'>*</Text></Text>}
                                                autoComplete='off'
                                                onChange={(e) => {
                                                    onPriceSettingsChange("sale_price", "end_date", e)
                                                    let tempErrors = JSON.parse(JSON.stringify(errors));
                                                    tempErrors['sale_price']['end_date'] = (e == "" || new Date(priceSettings.sale_price.start_date) > new Date(e));
                                                    setErrors(JSON.parse(JSON.stringify(tempErrors)));

                                                    let tempContextualSaveBarData = JSON.parse(JSON.stringify(contextualSaveBarData));
                                                    if (JSON.stringify(e) != JSON.stringify(savedPriceSettings?.sale_price?.end_date)) tempContextualSaveBarData['sale_price_end_date'] = true;
                                                    else tempContextualSaveBarData['sale_price_end_date'] = false;
                                                    setContextualSaveBarData(JSON.parse(JSON.stringify(tempContextualSaveBarData)));

                                                }}
                                                type="date"
                                                value={priceSettings.sale_price.end_date}
                                                min={startDateinString}
                                                error={errors?.sale_price?.end_date || new Date(priceSettings.sale_price.start_date) > new Date(priceSettings.sale_price.end_date)}
                                                disabled={(priceSettings.sale_price.start_date === "") || errors.sale_price.start_date || !priceSettings.settings_enabled || saveLoader || getLoader || !priceSettings.settings_selected.sale_price}
                                            />
                                        </FormLayout.Group>
                                    </FormLayout>
                                </BlockStack>
                            </div>
                        </BlockStack>
                    </div>
                </InlineStack>
                <InlineStack gap="200" wrap={false}>
                    <Switch
                        className='switcherBtn'
                        size='small'
                        id='price_Switch_MinimumPrice'
                        onChange={(e) => {
                            onPriceSettingsChange("settings_selected", "minimum_price", e);

                            let tempContextualSaveBarData = JSON.parse(JSON.stringify(contextualSaveBarData));
                            if (JSON.stringify(e) != JSON.stringify(savedPriceSettings['settings_selected']['minimum_price'])) tempContextualSaveBarData['minimum_price_switcher'] = true;
                            else tempContextualSaveBarData['minimum_price_switcher'] = false;
                            if (!e) {
                                tempContextualSaveBarData['minimum_price_attribute'] = false;
                                tempContextualSaveBarData['minimum_price_value'] = false;
                                tempContextualSaveBarData['minimum_price_roundOff'] = false;

                                let tempErrors = JSON.parse(JSON.stringify(errors));
                                tempErrors['minimum_price'] = defaultPriceErrors['minimum_price'];
                                setErrors(JSON.parse(JSON.stringify(tempErrors)));
                            }
                            else {
                                if (JSON.stringify(savedPriceSettings['minimum_price']['attribute']) != JSON.stringify(priceSettings['minimum_price']['attribute'])) tempContextualSaveBarData['minimum_price_attribute'] = true;
                                else tempContextualSaveBarData['minimum_price_attribute'] = false;

                                if (JSON.stringify(savedPriceSettings['minimum_price']['value']) != JSON.stringify(priceSettings['minimum_price']['value'])) tempContextualSaveBarData['minimum_price_value'] = true;
                                else tempContextualSaveBarData['minimum_price_value'] = false;

                                if (JSON.stringify(savedPriceSettings['minimum_price']['price_roundoff_value']) != JSON.stringify(priceSettings['minimum_price']['price_roundoff_value'])) tempContextualSaveBarData['minimum_price_roundOff'] = true;
                                else tempContextualSaveBarData['minimum_price_roundOff'] = false;
                            }
                            setContextualSaveBarData(JSON.parse(JSON.stringify(tempContextualSaveBarData)));
                        }}
                        checked={priceSettings.settings_selected.minimum_price}
                        disabled={!priceSettings.settings_enabled || saveLoader || getLoader} />
                    <div className='Custom__Fill--Alignment'>
                        <BlockStack gap="200">
                            <BlockStack gap="100">
                                <div className='Custom__Line--Height'>
                                    <Text variant="headingSm" as="h6" id='price_Text_MinimumPrice'>{t("Set Minimum Price")}</Text>
                                </div>
                                <Text as="p" fontWeight="regular" tone='subdued' id='price_Text_MinimumPriceDescription'>{t("Map or set a minimum price for your products")}</Text>
                            </BlockStack>
                            <div className='Custom__TextField--alignmnet'>
                                <FormLayout>
                                    <FormLayout.Group condensed>
                                        <Select
                                            id='price_Select_MinimumPrice_PricingTrend'
                                            label={!priceSettings.settings_enabled || saveLoader || getLoader || !priceSettings.settings_selected.minimum_price ? <Text as='p' tone='subdued'>{t("Pricing Trend")}<Text as='span' tone='critical'>*</Text></Text> : <Text as='p'>{t("Pricing Trend")}<Text as='span' tone='critical'>*</Text></Text>}
                                            options={trendoptionprice(t)}
                                            onChange={(e) => {
                                                onPriceSettingsChange("minimum_price", "attribute", e)

                                                let tempErrors = JSON.parse(JSON.stringify(errors));
                                                tempErrors['minimum_price']['attribute'] = e == "";
                                                tempErrors['minimum_price']['value'] = false;
                                                setErrors(JSON.parse(JSON.stringify(tempErrors)));

                                                let tempContextualSaveBarData = JSON.parse(JSON.stringify(contextualSaveBarData));
                                                if (JSON.stringify(e) != JSON.stringify(savedPriceSettings?.minimum_price?.attribute)) tempContextualSaveBarData['minimum_price_attribute'] = true;
                                                else tempContextualSaveBarData['minimum_price_attribute'] = false;
                                                setContextualSaveBarData(JSON.parse(JSON.stringify(tempContextualSaveBarData)));
                                            }}
                                            value={priceSettings.minimum_price.attribute}
                                            error={errors?.minimum_price?.attribute}
                                            disabled={!priceSettings.settings_enabled || saveLoader || getLoader || !priceSettings.settings_selected.minimum_price}
                                        />
                                        {priceSettings.minimum_price.attribute !== "" && priceSettings.minimum_price.attribute !== "default" &&
                                            <TextField
                                                id='price_TextField_MinimumPrice_PriceValue'
                                                label={!priceSettings.settings_enabled || saveLoader || getLoader || !priceSettings.settings_selected.minimum_price ? <Text as='p' tone='subdued'>{t("Price Value")}<Text as='span' tone='critical'>*</Text></Text> : <Text as='p'>{t("Price Value")}<Text as='span' tone='critical'>*</Text></Text>}
                                                autoComplete='off'
                                                onChange={(e) => {
                                                    e = e.replace(/[\sa-zA-Z-!$%^&*()@_+|~=`\\#{}\[\]:";'<>?,\/]/gi, '');
                                                    e = e?.[0] === '.' ? '0' + e : e;
                                                    e = (e.split(".").length - 1 > 1 ? e.slice(0, -1) : e).replace(/^(\d+.?\d{0,2})\d*$/, "$1");
                                                    onPriceSettingsChange("minimum_price", "value", e);

                                                    let tempErrors = JSON.parse(JSON.stringify(errors));
                                                    tempErrors['minimum_price']['value'] = e == "";
                                                    setErrors(JSON.parse(JSON.stringify(tempErrors)));

                                                    let tempContextualSaveBarData = JSON.parse(JSON.stringify(contextualSaveBarData));
                                                    if (JSON.stringify(e) != JSON.stringify(savedPriceSettings?.minimum_price?.value)) tempContextualSaveBarData['minimum_price_value'] = true;
                                                    else tempContextualSaveBarData['minimum_price_value'] = false;
                                                    setContextualSaveBarData(JSON.parse(JSON.stringify(tempContextualSaveBarData)));
                                                }}
                                                placeholder={t("Enter value")}
                                                value={priceSettings.minimum_price.value}
                                                error={errors?.minimum_price?.value}
                                                disabled={!priceSettings.settings_enabled || saveLoader || getLoader || !priceSettings.settings_selected.minimum_price}
                                            />
                                        }
                                        {priceSettings.minimum_price.attribute != '' && <Select
                                            id='price_Select_MinimumPrice_RoundingOff'
                                            label={!priceSettings.settings_enabled || saveLoader || getLoader || !priceSettings.settings_selected.minimum_price ? <Text as='p' tone='subdued'>{t("Rounding Off")}<Text as='span' tone='critical'>*</Text></Text> : <Text as='p'>{t("Rounding Off")}<Text as='span' tone='critical'>*</Text></Text>}
                                            // label={props.priceSettings.settings_enabled ? <Text as='span'>{t("Rounding Off")}<Text as='span' tone='critical'> *</Text></Text> : <Text as='span' tone="subdued">{t("Rounding Off")}<Text as='span' tone='critical'> *</Text></Text>}
                                            options={roundOffOptions}
                                            onChange={(e) => {
                                                onPriceSettingsChange("minimum_price", "price_roundoff_value", e);
                                                let tempContextualSaveBarData = JSON.parse(JSON.stringify(contextualSaveBarData));
                                                if (JSON.stringify(e) != JSON.stringify(savedPriceSettings['minimum_price']['price_roundoff_value'])) tempContextualSaveBarData['minimum_price_roundOff'] = true;
                                                else tempContextualSaveBarData['minimum_price_roundOff'] = false;
                                                setContextualSaveBarData(JSON.parse(JSON.stringify(tempContextualSaveBarData)));
                                            }}
                                            value={priceSettings?.minimum_price?.price_roundoff_value}
                                            // helpText={`Kinldy use 'Preview price-round off' feature for exact calculation. `}
                                            disabled={!priceSettings.settings_enabled || saveLoader || getLoader || !priceSettings.settings_selected.minimum_price}
                                        // error={context.errors.pricing_settings.standard_price.attribute}
                                        />
                                        }
                                    </FormLayout.Group>
                                </FormLayout>
                            </div>
                        </BlockStack>
                    </div>
                </InlineStack >
                {B2B_visible && <InlineStack gap="200" wrap={false}>
                    <Switch
                        className='switcherBtn'
                        size='small'
                        id='price_Switch_BusinessPrice'
                        onChange={(e) => {
                            onPriceSettingsChange("settings_selected", "business_price", e);
                            let tempErrors = JSON.parse(JSON.stringify(errors));
                            tempErrors['business_price'] = defaultPriceErrors['business_price'];
                            setErrors(JSON.parse(JSON.stringify(tempErrors)));

                            let tempContextualSaveBarData = JSON.parse(JSON.stringify(contextualSaveBarData));
                            if (JSON.stringify(e) != JSON.stringify(savedPriceSettings['settings_selected']['business_price'])) tempContextualSaveBarData['business_price_switcher'] = true;
                            else tempContextualSaveBarData['business_price_switcher'] = false;
                            if (!e) {
                                tempContextualSaveBarData['business_price_attribute'] = false;
                                tempContextualSaveBarData['business_price_value'] = false;
                                tempContextualSaveBarData['business_price_roundOff'] = false;

                                let tempErrors = JSON.parse(JSON.stringify(errors));
                                tempErrors['business_price'] = defaultPriceErrors['business_price'];
                                setErrors(JSON.parse(JSON.stringify(tempErrors)));
                            }
                            else {
                                if (JSON.stringify(savedPriceSettings['business_price']['attribute']) != JSON.stringify(priceSettings['business_price']['attribute'])) tempContextualSaveBarData['business_price_attribute'] = true;
                                else tempContextualSaveBarData['business_price_attribute'] = false;

                                if (JSON.stringify(savedPriceSettings['business_price']['value']) != JSON.stringify(priceSettings['business_price']['value'])) tempContextualSaveBarData['business_price_value'] = true;
                                else tempContextualSaveBarData['value'] = false;

                                if (JSON.stringify(savedPriceSettings['business_price']['price_roundoff_value']) != JSON.stringify(priceSettings['business_price']['price_roundoff_value'])) tempContextualSaveBarData['business_price_roundOff'] = true;
                                else tempContextualSaveBarData['business_price_roundOff'] = false;
                            }
                            setContextualSaveBarData(JSON.parse(JSON.stringify(tempContextualSaveBarData)));
                        }}
                        checked={priceSettings.settings_selected.business_price}
                        disabled={!priceSettings.settings_enabled || saveLoader || getLoader} />
                    <div className='Custom__Fill--Alignment'>
                        <BlockStack gap="200">
                            <BlockStack gap="100">
                                <div className='Custom__Line--Height'>
                                    <Text variant="headingSm" as="h6" id='price_Text_BusinessPrice'>{t("Set Business Price")}</Text>
                                </div>
                                <Text as="p" fontWeight="regular" tone='subdued' id='price_Text_BusinessPriceDescription'>{t("Establish discounted price for businesses, organizations, or individuals with a registered business account on Amazon.")}</Text>
                            </BlockStack>
                            <BlockStack gap={"400"}>
                                <div className='Custom__TextField--alignmnet'>
                                    <FormLayout>
                                        <FormLayout.Group condensed>
                                            <Select
                                                id='price_Select_BusinessPrice_PricingTrend'
                                                label={!priceSettings.settings_enabled || saveLoader || getLoader || !priceSettings.settings_selected.business_price ? <Text as='p' tone='subdued'>{t("Pricing Trend")}<Text as='span' tone='critical'>*</Text></Text> : <Text as='p'>{t("Pricing Trend")}<Text as='span' tone='critical'>*</Text></Text>}
                                                options={trendOptionBusinessPrice(t)}
                                                onChange={(e) => {
                                                    onPriceSettingsChange("business_price", "attribute", e)
                                                    let tempErrors = JSON.parse(JSON.stringify(errors));
                                                    tempErrors['business_price']['attribute'] = e == "";
                                                    tempErrors['business_price']['value'] = false;
                                                    setErrors(JSON.parse(JSON.stringify(tempErrors)));

                                                    let tempContextualSaveBarData = JSON.parse(JSON.stringify(contextualSaveBarData));
                                                    if (JSON.stringify(e) != JSON.stringify(savedPriceSettings?.business_price?.attribute)) tempContextualSaveBarData['business_price_attribute'] = true;
                                                    else tempContextualSaveBarData['business_price_attribute'] = false;
                                                    setContextualSaveBarData(JSON.parse(JSON.stringify(tempContextualSaveBarData)));
                                                }}
                                                value={priceSettings.business_price.attribute}
                                                error={errors?.business_price?.attribute}
                                                disabled={!priceSettings.settings_enabled || saveLoader || getLoader || !priceSettings.settings_selected.business_price}
                                            />
                                            {priceSettings.business_price.attribute !== "" && priceSettings.business_price.attribute !== "default" &&
                                                <TextField
                                                    id='price_TextField_BusinessPrice_PriceValue'
                                                    label={!priceSettings.settings_enabled || saveLoader || getLoader || !priceSettings.settings_selected.business_price ? <Text as='p' tone='subdued'>{priceSettings.business_price.attribute == "percentage_decrease" ? t("Price Percentage") : t("Price Value")}<Text as='span' tone='critical'>*</Text></Text> : <Text as='p'>{priceSettings.business_price.attribute == "percentage_decrease" ? t("Price Percentage") : t("Price Value")}<Text as='span' tone='critical'>*</Text></Text>}
                                                    autoComplete='off'
                                                    onChange={(e) => {
                                                        e = e.replace(/[\sa-zA-Z-!$%^&*()@_+|~=`\\#{}\[\]:";'<>?,\/]/gi, '');
                                                        e = e?.[0] === '.' ? '0' + e : e;
                                                        e = (e.split(".").length - 1 > 1 ? e.slice(0, -1) : e).replace(/^(\d+.?\d{0,2})\d*$/, "$1");
                                                        onPriceSettingsChange("business_price", "value", e)
                                                        let tempErrors = JSON.parse(JSON.stringify(errors));
                                                        tempErrors['business_price']['value'] = e == "";
                                                        setErrors(JSON.parse(JSON.stringify(tempErrors)));

                                                        let tempContextualSaveBarData = JSON.parse(JSON.stringify(contextualSaveBarData));
                                                        if (JSON.stringify(e) != JSON.stringify(savedPriceSettings?.business_price?.value)) tempContextualSaveBarData['business_price_value'] = true;
                                                        else tempContextualSaveBarData['business_price_value'] = false;
                                                        setContextualSaveBarData(JSON.parse(JSON.stringify(tempContextualSaveBarData)));
                                                    }}
                                                    placeholder={t("Enter value")}
                                                    value={priceSettings.business_price.value}
                                                    error={errors?.business_price?.value}
                                                    disabled={!priceSettings.settings_enabled || saveLoader || getLoader || !priceSettings.settings_selected.business_price}
                                                />
                                            }
                                            {priceSettings.business_price.attribute != '' && priceSettings.business_price.value != '' && <Select
                                                id='price_Select_BusinessPrice_RoundingOff'
                                                label={!priceSettings.settings_enabled || saveLoader || getLoader || !priceSettings.settings_selected.business_price ? <Text as='p' tone='subdued'>{t("Rounding Off")}<Text as='span' tone='critical'>*</Text></Text> : <Text as='p'>{t("Rounding Off")}<Text as='span' tone='critical'>*</Text></Text>}
                                                options={roundOffOptions}
                                                onChange={(e) => {
                                                    onPriceSettingsChange("business_price", "price_roundoff_value", e);
                                                    let tempContextualSaveBarData = JSON.parse(JSON.stringify(contextualSaveBarData));
                                                    if (JSON.stringify(e) != JSON.stringify(savedPriceSettings['business_price']['price_roundoff_value'])) tempContextualSaveBarData['business_price' + '_roundOff'] = true;
                                                    else tempContextualSaveBarData['business_price' + '_roundOff'] = false;
                                                    setContextualSaveBarData(JSON.parse(JSON.stringify(tempContextualSaveBarData)));
                                                }}
                                                value={priceSettings?.business_price?.price_roundoff_value}
                                                disabled={!priceSettings.settings_enabled || saveLoader || getLoader || !priceSettings.settings_selected.business_price}
                                            />}
                                        </FormLayout.Group>
                                    </FormLayout>
                                </div>
                                {priceSettings.settings_enabled && priceSettings.settings_selected.business_price && priceSettings.business_price.attribute != "" && priceSettings.business_price.value != "" &&
                                    <AddDiscounts businessPrice={priceSettings.business_price} setPriceSettings={setPriceSettings} contextualSaveBarData={contextualSaveBarData} setContextualSaveBarData={setContextualSaveBarData} savedPriceSettings={savedPriceSettings} myErrors={errors} setErrors={setErrors} showBusinessPrice={showBusinessPrice} setShowBusinessPrice={setShowBusinessPrice} savedShowBusinessPrice={savedShowBusinessPrice} />}
                            </BlockStack>
                        </BlockStack>
                    </div>
                </InlineStack >}
                {lastSync && <>
                    <Divider />
                    <InlineStack blockAlign='center' align='space-between' gap="200">
                        <div className='Custom__Sync--Update'>
                            <InlineStack gap="200" wrap={false}>
                                <div>
                                    <Icon source={RefreshMajor} tone="base" />
                                </div>
                                <Text variant="headingSm" as="h6" tone='subdued'>
                                    {t("Last Sync Update")}: {lastSync}
                                </Text>
                            </InlineStack>
                        </div>
                    </InlineStack>
                </>
                }
            </BlockStack >
        </>}
    </>
}

export default DI(InventorySettings);